import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { LayoutGrid, ArrowRight } from "lucide-react";

const FILTER_LABELS = { awards: "Awards", commitments: "Commitments", rankings: "Rankings", featured: "Featured", metrics: "Metrics" };

export default function MediaWall() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = active === "all" ? {} : { filter: active };
    api.get("/media-wall", { params }).then(({ data }) => { setItems(data.items || []); setFilters(data.filters || []); }).catch(() => setItems([])).finally(() => setLoading(false));
  }, [active]);

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><LayoutGrid className="w-4 h-4" /> Gallery</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-3">CBR Media Wall</h1>
          <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">Verified achievements, commitments, awards and rankings — the official CBR Prospects graphics feed.</p>
        </div>
      </section>

      <section className="bg-[#F8F9FA] min-h-[50vh] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-1 mb-8" data-testid="mw-filters">
            {["all", ...filters].map((f) => (
              <button key={f} data-testid={`mw-filter-${f}`} onClick={() => setActive(f)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors ${active === f ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#0A192F]"}`}>
                {f === "all" ? "All" : (FILTER_LABELS[f] || f)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center text-sm text-slate-500">No graphics published yet. Check back soon.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="mw-grid">
              {items.map((it) => {
                const Card = (
                  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0A192F] transition-colors group">
                    <div className="aspect-square bg-[#0A192F] relative">
                      <img src={it.card_image_url} alt={it.player_name || "CBR graphic"} className="w-full h-full object-cover" loading="lazy" />
                      <span className="absolute top-1.5 left-1.5 text-[9px] font-semibold uppercase tracking-wider bg-[#E63946] text-white px-1.5 py-0.5 rounded-sm">{FILTER_LABELS[it.category] || it.category}</span>
                    </div>
                    <div className="p-2.5">
                      <div className="text-xs font-semibold truncate group-hover:text-[#E63946]">{it.player_name || "—"}</div>
                      {it.event_name && <div className="text-[10px] text-slate-400 truncate">{it.event_name}</div>}
                    </div>
                  </div>
                );
                return it.link
                  ? <Link key={it.id} to={it.link} data-testid={`mw-item-${it.id}`}>{Card}</Link>
                  : <div key={it.id} data-testid={`mw-item-${it.id}`}>{Card}</div>;
              })}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
